import DemoBadge from '@/components/feature/DemoBadge';
import {
  LANGUAGE_DISTRIBUTION,
  SUBSCRIBERS_BY_LANGUAGE,
  ENGAGEMENT_BY_LANGUAGE,
  NEWSLETTER_BY_LANGUAGE,
  TRANSLATION_COVERAGE,
  READERSHIP_BY_REGION,
  overallCoveragePct,
  translationCoveragePct,
  TOTAL_STORIES,
  TOTAL_TRANSLATED,
  regionLabelFromId,
} from '@/lib/multilingual';
import StatusBadge from './StatusBadge';

const fmt = (n: number) => n.toLocaleString('en-US');

function Kpi({ label, value, note, icon }: { label: string; value: string; note?: string; icon: string }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
      <div className="flex items-center justify-between gap-2">
        <p className="font-label uppercase tracking-widest text-[10px] md:text-[11px] text-foreground-500">{label}</p>
        <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-600 flex items-center justify-center shrink-0"><i className={icon}></i></span>
      </div>
      <p className="mt-2.5 font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 leading-none">{value}</p>
      {note && <p className="mt-1.5 text-[11px] text-foreground-500">{note}</p>}
    </div>
  );
}

function BarRow({ label, value, max, suffix }: { label: string; value: number; max: number; suffix?: string }) {
  return (
    <li>
      <div className="flex items-center justify-between text-sm">
        <span className="text-foreground-800 truncate">{label}</span>
        <span className="font-label font-semibold text-foreground-600 whitespace-nowrap">{suffix ? `${value}${suffix}` : fmt(value)}</span>
      </div>
      <div className="mt-1.5 h-2 rounded-full bg-background-200 overflow-hidden">
        <div className="h-full bg-accent-500 rounded-full" style={{ width: `${max ? (value / max) * 100 : 0}%` }}></div>
      </div>
    </li>
  );
}

export default function LanguageAnalytics() {
  const spanishSubs = SUBSCRIBERS_BY_LANGUAGE.find((s) => s.lang === 'Spanish')?.count ?? 0;
  const spanishNews = NEWSLETTER_BY_LANGUAGE.find((s) => s.lang === 'Spanish');
  const totalSubs = SUBSCRIBERS_BY_LANGUAGE.reduce((a, s) => a + s.count, 0);
  const distMax = Math.max(...LANGUAGE_DISTRIBUTION.map((d) => d.pct));
  const subsMax = Math.max(...SUBSCRIBERS_BY_LANGUAGE.map((s) => s.count));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-heading font-extrabold uppercase text-xl md:text-2xl text-foreground-950">Language analytics</h2>
        <DemoBadge label="Sample audience data — not live" />
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi label="Translation coverage" value={`${overallCoveragePct()}%`} note={`${fmt(TOTAL_TRANSLATED)} of ${fmt(TOTAL_STORIES)} sample stories`} icon="ri-translate-2" />
        <Kpi label="Spanish preference" value="17%" note="Self-selected reading language" icon="ri-user-heart-line" />
        <Kpi label="Spanish subscribers" value={fmt(spanishSubs)} note={`of ${fmt(totalSubs)} sample subscribers`} icon="ri-mail-line" />
        <Kpi label="Spanish open rate" value={`${spanishNews?.openRate ?? 0}%`} note="Sample newsletter performance" icon="ri-mail-open-line" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Preferred language distribution */}
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <div className="flex items-center justify-between gap-2">
            <p className="font-heading font-bold text-lg text-foreground-950">Preferred language</p>
            <StatusBadge tone="neutral">Self-selected</StatusBadge>
          </div>
          <ul className="mt-4 space-y-3.5">
            {LANGUAGE_DISTRIBUTION.map((d) => (
              <BarRow key={d.lang} label={d.lang} value={d.pct} max={distMax} suffix="%" />
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-foreground-500">Based on the language each reader chooses — never inferred from location or ethnicity.</p>
        </div>

        {/* Subscribers by language */}
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <p className="font-heading font-bold text-lg text-foreground-950">Newsletter subscribers by language</p>
          <ul className="mt-4 space-y-3.5">
            {SUBSCRIBERS_BY_LANGUAGE.map((s) => (
              <BarRow key={s.lang} label={s.lang} value={s.count} max={subsMax} />
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-foreground-500">Sample counts only — no subscriber records are read or changed.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Content engagement */}
        <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
          <div className="px-4 md:px-5 py-3.5 border-b border-background-200">
            <p className="font-heading font-bold text-lg text-foreground-950">Content engagement by language</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[440px]">
              <thead>
                <tr className="text-left font-label uppercase tracking-widest text-[10px] text-foreground-500 border-b border-background-200">
                  <th className="px-4 py-2.5 font-semibold">Language</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Page views</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Sessions</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Avg read</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {ENGAGEMENT_BY_LANGUAGE.map((r) => (
                  <tr key={r.lang}>
                    <td className="px-4 py-3 text-foreground-950 font-medium whitespace-nowrap">{r.lang}</td>
                    <td className="px-3 py-3 text-right text-foreground-700">{fmt(r.pageViews)}</td>
                    <td className="px-3 py-3 text-right text-foreground-700">{fmt(r.sessions)}</td>
                    <td className="px-4 py-3 text-right text-foreground-700">{r.avgReadSeconds}s</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Newsletter performance */}
        <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
          <div className="px-4 md:px-5 py-3.5 border-b border-background-200">
            <p className="font-heading font-bold text-lg text-foreground-950">Newsletter performance by language</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[440px]">
              <thead>
                <tr className="text-left font-label uppercase tracking-widest text-[10px] text-foreground-500 border-b border-background-200">
                  <th className="px-4 py-2.5 font-semibold">Language</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Sent</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Open rate</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Click rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {NEWSLETTER_BY_LANGUAGE.map((r) => (
                  <tr key={r.lang}>
                    <td className="px-4 py-3 text-foreground-950 font-medium whitespace-nowrap">{r.lang}</td>
                    <td className="px-3 py-3 text-right text-foreground-700">{fmt(r.sent)}</td>
                    <td className="px-3 py-3 text-right text-foreground-700">{r.openRate}%</td>
                    <td className="px-4 py-3 text-right text-foreground-700">{r.clickRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Translation coverage by region */}
        <div className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5">
          <p className="font-heading font-bold text-lg text-foreground-950">Translation coverage by region</p>
          <ul className="mt-4 space-y-3.5">
            {TRANSLATION_COVERAGE.map((r) => (
              <li key={r.regionId}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground-800 truncate">{regionLabelFromId(r.regionId)}</span>
                  <span className="font-label font-semibold text-foreground-600 whitespace-nowrap">{r.translated}/{r.stories} · {translationCoveragePct(r)}%</span>
                </div>
                <div className="mt-1.5 h-2 rounded-full bg-background-200 overflow-hidden">
                  <div className="h-full bg-primary-500 rounded-full" style={{ width: `${translationCoveragePct(r)}%` }}></div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Regional readership */}
        <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
          <div className="px-4 md:px-5 py-3.5 border-b border-background-200">
            <p className="font-heading font-bold text-lg text-foreground-950">Regional readership</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[400px]">
              <thead>
                <tr className="text-left font-label uppercase tracking-widest text-[10px] text-foreground-500 border-b border-background-200">
                  <th className="px-4 py-2.5 font-semibold">Region</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Sessions</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Spanish share</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200">
                {READERSHIP_BY_REGION.map((r) => (
                  <tr key={r.regionId}>
                    <td className="px-4 py-3 text-foreground-950 font-medium whitespace-nowrap">{regionLabelFromId(r.regionId)}</td>
                    <td className="px-3 py-3 text-right text-foreground-700">{fmt(r.sessions)}</td>
                    <td className="px-4 py-3 text-right text-foreground-700">{r.spanishShare}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <p className="text-xs text-foreground-500 flex items-center gap-1.5">
        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-flask-line"></i></span>
        One internally consistent sample dataset. No sensitive demographic characteristics are inferred from reader behaviour.
      </p>
    </div>
  );
}